import sodium from 'libsodium-wrappers';

export interface KeyPair {
  publicKey: string;
  secretKey: string;
}

// Generate a new keypair for the user
export const generateKeyPair = (): KeyPair => {
  const keyPair = sodium.crypto_box_keypair();
  return {
    publicKey: sodium.to_base64(keyPair.publicKey, sodium.base64_variants.ORIGINAL),
    secretKey: sodium.to_base64(keyPair.privateKey, sodium.base64_variants.ORIGINAL),
  };
};

// Encrypt a message for a recipient
export const encryptMessage = (
  message: string,
  recipientPublicKey: string,
  senderSecretKey: string,
): string | null => {
  try {
    const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
    const messageUint8 = sodium.from_string(message);
    const recipientPublicKeyUint8 = sodium.from_base64(
      recipientPublicKey,
      sodium.base64_variants.ORIGINAL,
    );
    const senderSecretKeyUint8 = sodium.from_base64(
      senderSecretKey,
      sodium.base64_variants.ORIGINAL,
    );

    const encrypted = sodium.crypto_box_easy(
      messageUint8,
      nonce,
      recipientPublicKeyUint8,
      senderSecretKeyUint8,
    );

    const fullMessage = new Uint8Array(nonce.length + encrypted.length);
    fullMessage.set(nonce);
    fullMessage.set(encrypted, nonce.length);

    return sodium.to_base64(fullMessage, sodium.base64_variants.ORIGINAL);
  } catch (error) {
    console.error('Encryption error:', error);
    return null;
  }
};

// Decrypt a message from a sender
export const decryptMessage = (
  encryptedMessage: string,
  senderPublicKey: string,
  recipientSecretKey: string,
): string | null => {
  try {
    const messageWithNonceUint8 = sodium.from_base64(
      encryptedMessage,
      sodium.base64_variants.ORIGINAL,
    );
    const nonce = messageWithNonceUint8.slice(0, sodium.crypto_box_NONCEBYTES);
    const message = messageWithNonceUint8.slice(sodium.crypto_box_NONCEBYTES);
    const senderPublicKeyUint8 = sodium.from_base64(
      senderPublicKey,
      sodium.base64_variants.ORIGINAL,
    );
    const recipientSecretKeyUint8 = sodium.from_base64(
      recipientSecretKey,
      sodium.base64_variants.ORIGINAL,
    );

    const decrypted = sodium.crypto_box_open_easy(
      message,
      nonce,
      senderPublicKeyUint8,
      recipientSecretKeyUint8,
    );

    return sodium.to_string(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

// Encrypt audio data for voice channels (symmetric encryption)
export const encryptAudioData = (
  audioData: ArrayBuffer,
  channelKey: string,
): ArrayBuffer | null => {
  try {
    const keyUint8 = sodium.from_base64(channelKey, sodium.base64_variants.ORIGINAL);
    const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
    const dataUint8 = new Uint8Array(audioData);

    const encrypted = sodium.crypto_secretbox_easy(dataUint8, nonce, keyUint8);

    const result = new Uint8Array(nonce.length + encrypted.length);
    result.set(nonce);
    result.set(encrypted, nonce.length);

    return result.buffer;
  } catch (error) {
    console.error('Audio encryption error:', error);
    return null;
  }
};

// Decrypt audio data for voice channels
export const decryptAudioData = (
  encryptedData: ArrayBuffer,
  channelKey: string,
): ArrayBuffer | null => {
  try {
    const keyUint8 = sodium.from_base64(channelKey, sodium.base64_variants.ORIGINAL);
    const dataUint8 = new Uint8Array(encryptedData);
    const nonce = dataUint8.slice(0, sodium.crypto_secretbox_NONCEBYTES);
    const encrypted = dataUint8.slice(sodium.crypto_secretbox_NONCEBYTES);

    const decrypted = sodium.crypto_secretbox_open_easy(encrypted, nonce, keyUint8);

    const copy = new Uint8Array(decrypted.length);
    copy.set(decrypted);
    return copy.buffer;
  } catch (error) {
    console.error('Audio decryption error:', error);
    return null;
  }
};

// Generate a symmetric key for voice channel encryption
export const generateChannelKey = (): string => {
  const key = sodium.randombytes_buf(sodium.crypto_secretbox_KEYBYTES);
  return sodium.to_base64(key, sodium.base64_variants.ORIGINAL);
};

// Key management helpers
const ENCRYPTION_KEY_STORAGE_KEY = 'user_encryption_secret_key';
const VOICE_ENCRYPTION_ENABLED_KEY = 'voice_encryption_enabled';

export const storeSecretKey = (secretKey: string): void => {
  // In production, consider using more secure storage (e.g., encrypted localStorage)
  localStorage.setItem(ENCRYPTION_KEY_STORAGE_KEY, secretKey);
};

export const getSecretKey = (): string | null => {
  return localStorage.getItem(ENCRYPTION_KEY_STORAGE_KEY);
};

export const clearSecretKey = (): void => {
  localStorage.removeItem(ENCRYPTION_KEY_STORAGE_KEY);
};

// Check if encryption is enabled
export const isEncryptionEnabled = (): boolean => {
  return getSecretKey() !== null;
};

// Voice encryption settings
export const setVoiceEncryptionEnabled = (enabled: boolean): void => {
  localStorage.setItem(VOICE_ENCRYPTION_ENABLED_KEY, enabled.toString());
};

export const isVoiceEncryptionEnabled = (): boolean => {
  const setting = localStorage.getItem(VOICE_ENCRYPTION_ENABLED_KEY);
  // Default to true for security unless explicitly disabled
  return setting === null ? true : setting === 'true';
};

// Encrypt a file (as ArrayBuffer) for a recipient
export const encryptFile = (
  fileData: ArrayBuffer,
  recipientPublicKey: string,
  senderSecretKey: string,
): Blob | null => {
  try {
    const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
    const fileUint8 = new Uint8Array(fileData);
    const recipientPublicKeyUint8 = sodium.from_base64(
      recipientPublicKey,
      sodium.base64_variants.ORIGINAL,
    );
    const senderSecretKeyUint8 = sodium.from_base64(
      senderSecretKey,
      sodium.base64_variants.ORIGINAL,
    );

    const encrypted = sodium.crypto_box_easy(
      fileUint8,
      nonce,
      recipientPublicKeyUint8,
      senderSecretKeyUint8,
    );

    const fullMessage = new Uint8Array(nonce.length + encrypted.length);
    fullMessage.set(nonce);
    fullMessage.set(encrypted, nonce.length);

    return new Blob([fullMessage.buffer]);
  } catch (error) {
    console.error('File encryption error:', error);
    return null;
  }
};

// Decrypt a file (as ArrayBuffer) from a sender
export const decryptFile = (
  encryptedFileData: ArrayBuffer,
  senderPublicKey: string,
  recipientSecretKey: string,
): Blob | null => {
  try {
    const fileWithNonceUint8 = new Uint8Array(encryptedFileData);
    const nonce = fileWithNonceUint8.slice(0, sodium.crypto_box_NONCEBYTES);
    const file = fileWithNonceUint8.slice(sodium.crypto_box_NONCEBYTES);

    const senderPublicKeyUint8 = sodium.from_base64(
      senderPublicKey,
      sodium.base64_variants.ORIGINAL,
    );
    const recipientSecretKeyUint8 = sodium.from_base64(
      recipientSecretKey,
      sodium.base64_variants.ORIGINAL,
    );

    const decrypted = sodium.crypto_box_open_easy(
      file,
      nonce,
      senderPublicKeyUint8,
      recipientSecretKeyUint8,
    );

    const copy = new Uint8Array(decrypted.length);
    copy.set(decrypted);
    return new Blob([copy.buffer]);
  } catch (error) {
    console.error('File decryption error:', error);
    return null;
  }
};
